import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarBlank, MapPin } from "@phosphor-icons/react";
import { Reveal, Stagger, fadeItem } from "@/components/site/Reveal";
import { motion } from "framer-motion";
import { UPCOMING_EVENTS, FAMILY_BUSINESS_CONCLAVE } from "@/lib/events";
import { EVENTS as ANNUAL_EVENTS } from "@/lib/content";

export default function EventsHub() {
  return (
    <main data-testid="events-hub-page" className="bg-parchment text-ocean">
      {/* HERO */}
      <section className="pt-40 pb-12 md:pt-48 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <div className="overline text-spice">Events</div>
            <h1 className="font-serif-display text-6xl md:text-8xl mt-6 leading-[0.85]">
              Where Kozhikode
              <br />
              <em className="text-spice">shows up.</em>
            </h1>
          </div>
          <div className="md:col-span-5 self-end">
            <p className="text-ocean/75 text-lg leading-relaxed">
              The chapter's calendar — a flagship conclave on the horizon, plus the signature
              gatherings that anchor every Yi year on the Malabar coast.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED — Upcoming events */}
      <section className="py-16 md:py-24 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline text-spice mb-2">On the horizon</div>
          <h2 className="font-serif-display text-3xl md:text-5xl mb-12">Upcoming</h2>

          <Stagger className="grid grid-cols-1 gap-6">
            {UPCOMING_EVENTS.map((e) => (
              <motion.article
                key={e.slug}
                variants={fadeItem}
                data-testid={`upcoming-event-${e.slug}`}
                className="relative grid md:grid-cols-12 gap-8 border border-black/10 bg-parchment p-8 md:p-12 hover:border-spice transition-colors group"
              >
                <div className="md:col-span-3 flex md:flex-col gap-4 md:gap-6">
                  <div>
                    <div className="overline text-brass">{e.dateOverline}</div>
                    <div className="font-serif text-3xl md:text-4xl mt-2 flex items-center gap-2">
                      <CalendarBlank size={24} weight="light" className="text-spice" />
                      {e.date}
                    </div>
                  </div>
                  <div className="text-ocean/65 text-sm flex items-start gap-2">
                    <MapPin size={16} weight="light" className="text-spice mt-1 shrink-0" />
                    {e.venue}
                  </div>
                </div>

                <div className="md:col-span-9">
                  <h3 className="font-serif-display text-4xl md:text-5xl leading-tight">
                    {e.title}
                  </h3>
                  <p className="italic text-spice text-lg md:text-xl mt-2">{e.tagline}</p>
                  <p className="mt-5 text-ocean/75 leading-relaxed max-w-2xl">{e.about}</p>

                  <Link
                    to={`/events/${e.slug}`}
                    data-testid={`upcoming-event-cta-${e.slug}`}
                    className="overline mt-8 inline-flex items-center gap-2 px-6 py-3 bg-ocean text-parchment hover:bg-spice transition-colors"
                  >
                    Read more &amp; register <ArrowUpRight size={16} weight="bold" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ANNUAL SIGNATURE EVENTS */}
      <section className="py-20 md:py-28 bg-ocean text-parchment relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline text-brass">A year in motion</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-4">
              Signature
              <br />
              <em className="text-brass">moments.</em>
            </h2>
            <p className="mt-6 text-parchment/70 leading-relaxed">
              The recurring gatherings that compound learning, friendships and impact — every Yi year.
            </p>
          </div>
          <div className="md:col-span-8">
            <ul>
              {ANNUAL_EVENTS.map((e, i) => (
                <Reveal key={e.name} delay={i}>
                  <li
                    data-testid={`annual-event-${i}`}
                    className="grid grid-cols-12 gap-6 py-8 border-t border-white/15 last:border-b items-center px-2"
                  >
                    <div className="col-span-1 font-serif text-2xl text-brass">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="col-span-11 md:col-span-4">
                      <div className="font-serif text-2xl md:text-3xl">{e.name}</div>
                    </div>
                    <div className="col-span-12 md:col-span-7 text-parchment/70 text-sm md:text-base leading-relaxed">
                      {e.blurb}
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="overline text-spice">Want to host with us?</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-4 leading-[0.95]">
              Sponsor a session, propose a panel, or just stop by KRS Hall on{" "}
              <em className="text-spice">{FAMILY_BUSINESS_CONCLAVE.date}</em>.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="events-hub-cta-contact"
              className="overline px-8 py-5 bg-ocean text-parchment hover:bg-spice transition-colors flex items-center gap-2"
            >
              Get in touch <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
